<script lang="ts">
  import { t } from '../i18n/index.svelte';
  import { decodeImage } from '../image';
  import { assets, studio, type SourceKind } from '../state.svelte';
  import { mlStatus } from '../ml/client.svelte';
  import { sceneStore } from '../scene/store.svelte';
  import { ui } from '../ui.svelte';
  import { estimate } from '../server/api';
  import { computeTarget, serverModels } from '../server/compute.svelte';
  import { formatDuration } from '../i18n/index.svelte';
  import { interactivePool } from '../wasm/pool';
  import FileButton from './ui/FileButton.svelte';
  import Section from './ui/Section.svelte';
  import Select from './ui/Select.svelte';
  import Slider from './ui/Slider.svelte';
  import TextFields from './TextFields.svelte';

  let error = $state('');

  const kinds: { id: SourceKind; key: 'source.text' | 'source.image' | 'source.depth' | 'source.scene' }[] = [
    { id: 'text', key: 'source.text' },
    { id: 'image', key: 'source.image' },
    { id: 'depth', key: 'source.depth' },
    { id: 'scene', key: 'source.scene' },
  ];

  async function onImage(f: File) {
    error = '';
    try {
      assets.sourceImage = await decodeImage(f);
      assets.sourceFile = f;
      assets.sourceName = f.name;
    } catch (e) {
      error = t('error.decode', { msg: String(e) });
    }
  }

  async function onDepth(f: File) {
    error = '';
    try {
      if (f.type === 'image/png' || f.name.toLowerCase().endsWith('.png')) {
        // PNG16 keeps full precision; decoded in WASM.
        const bytes = new Uint8Array(await f.arrayBuffer());
        assets.uploadedDepth = await interactivePool().call('decodeDepthPng', { bytes });
      } else {
        const img = await decodeImage(f);
        assets.uploadedDepth = await interactivePool().call('depthFromImage', {
          image: img,
          heuristic: { kind: 'brightness' },
        });
      }
      assets.sourceName = f.name;
    } catch (e) {
      error = t('error.decode', { msg: String(e) });
    }
  }

  const fix2 = (v: number) => v.toFixed(2);

  // Server-side ETA before running (depth estimation for the current image).
  let serverEstimate = $state<number | null>(null);
  $effect(() => {
    const img = assets.sourceImage;
    const model = studio.serverModel;
    if (!img || studio.heuristic !== 'ml' || computeTarget() !== 'server') {
      serverEstimate = null;
      return;
    }
    const ctl = new AbortController();
    estimate({ kind: 'depth', model, width: img.width, height: img.height }, ctl.signal)
      .then((e) => (serverEstimate = e.eta_ms))
      .catch(() => (serverEstimate = null));
    return () => ctl.abort();
  });
</script>

<Section title={t('source.title')}>
  <div class="seg" role="tablist">
    {#each kinds as k (k.id)}
      <button
        role="tab"
        aria-selected={studio.sourceKind === k.id}
        class:on={studio.sourceKind === k.id}
        data-testid={`source-${k.id}`}
        onclick={() => (studio.sourceKind = k.id)}>{t(k.key)}</button
      >
    {/each}
  </div>

  {#if studio.sourceKind === 'text'}
    <TextFields />
  {:else if studio.sourceKind === 'image'}
    <FileButton label={t('source.dropImage')} accept="image/*" onfile={onImage} testid="image-input" />
    {#if assets.sourceImage}
      <p class="muted small">{assets.sourceName} · {assets.sourceImage.width}×{assets.sourceImage.height}</p>
    {/if}
    <Select
      label={t('source.method')}
      bind:value={studio.heuristic}
      options={[
        { value: 'ml', label: t('heuristic.ml') },
        { value: 'brightness', label: t('heuristic.brightness') },
        { value: 'edges', label: t('heuristic.edges') },
      ]}
    />
    {#if studio.heuristic === 'edges'}
      <Slider
        label={t('heuristic.threshold')}
        bind:value={studio.edgeThreshold}
        min={0.02}
        max={0.9}
        step={0.01}
        format={fix2}
      />
    {/if}
    {#if studio.heuristic === 'ml' && computeTarget() === 'server'}
      <Select
        label={t('server.model')}
        bind:value={studio.serverModel}
        options={serverModels().map((m) => ({
          value: m.id,
          label: `${m.name ?? m.id}${m.backend ? ` · ${m.backend}` : ''}`,
        }))}
        testid="server-model"
      />
      <p class="muted small" data-testid="server-estimate">
        {t('server.mlInfo')}
        {#if serverEstimate !== null}· {t('eta.about', { t: formatDuration(serverEstimate) })}{/if}
      </p>
      {#if serverEstimate !== null && serverEstimate > 15000}
        <p class="small warn">{t('server.slowHint')}</p>
      {/if}
    {:else if studio.heuristic === 'ml'}
      <p class="muted small" data-testid="ml-info">
        {t('ml.info')}
        {#if mlStatus.backend}· {t('ml.backend', { backend: mlStatus.backend })}{/if}
        {#if mlStatus.lastMs}· {Math.round(mlStatus.lastMs)} ms{/if}
      </p>
      {#if mlStatus.error}<p class="error small">{mlStatus.error}</p>{/if}
    {:else}
      <p class="muted small">{t('source.mlHint')}</p>
    {/if}
  {:else if studio.sourceKind === 'scene'}
    <p class="muted small">
      {t('source.sceneHint', { n: sceneStore.current.nodes.length, t: sceneStore.time.toFixed(2) })}
    </p>
    <button onclick={() => (ui.view = 'scene')} data-testid="open-scene-editor">{t('source.openEditor')}</button>
  {:else}
    <FileButton label={t('source.dropDepth')} accept="image/png,image/*" onfile={onDepth} testid="depth-input" />
    {#if assets.uploadedDepth}
      <p class="muted small">{assets.sourceName} · {assets.uploadedDepth.width}×{assets.uploadedDepth.height}</p>
    {/if}
    <p class="muted small">{t('source.depthHint')}</p>
  {/if}
  {#if error}<p class="error small">{error}</p>{/if}
</Section>

<style>
  .warn {
    color: var(--accent);
  }
  .small {
    font-size: 12px;
    margin: 0;
  }
</style>
